import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Space, 
  Popconfirm, 
  message,
  Tag,
  Form,
  Input,
  Select,
  Cascader,
  Row,
  Col,
  Card
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { getApartmentList, deleteApartment, getApartmentDetail } from '../../api/apartment';
import { getDistrictList, getSubwayList } from '../../api/common';
import ApartmentModal from './components/ApartmentModal';
import './index.css';

const ApartmentList = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [subwayOptions, setSubwayOptions] = useState([]);
  const [districtMap, setDistrictMap] = useState({}); // 行政区代码到名称的映射
  const [subwayMap, setSubwayMap] = useState({}); // 地铁站代码到名称的映射
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  // 获取房源列表
  const fetchApartmentList = async (page = 1, pageSize = 10, filters = {}) => {
    setLoading(true);
    try {
      // 构建查询参数
      const params = {
        page: {
          pageNum: page,
          pageSize: pageSize
        },
        ...filters
      };
      
      const result = await getApartmentList(params);
      
      if (result.code === 200 && result.data) {
        // 设置房源列表数据
        setDataSource(result.data.houseList || []);
        
        // 设置分页信息
        const pageInfo = result.data.pageInfo || {};
        setPagination({
          current: pageInfo.pageNum || page,
          pageSize: pageInfo.pageSize || pageSize,
          total: pageInfo.totalCount || 0
        });
      }
    } catch (error) {
      message.error('获取房源列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取行政区和地铁站选项
  const fetchOptions = async () => {
    try {
      // 获取行政区列表
      const districtResult = await getDistrictList();
      if (districtResult.code === 200 && districtResult.data) {
        // 转换为 Select 需要的格式：{ label, value }
        const districtList = districtResult.data.map(item => ({
          label: item.name,
          value: item.code
        }));
        setDistrictOptions(districtList);
        
        // 构建行政区代码到名称的映射
        const districtMapping = {};
        districtResult.data.forEach(item => {
          districtMapping[item.code] = item.name;
        });
        setDistrictMap(districtMapping);
      }

      // 获取地铁站列表
      const subwayResult = await getSubwayList();
      if (subwayResult.code === 200 && subwayResult.data) {
        // 转换为 Cascader 需要的格式：{ label, value, children }
        const subwayList = subwayResult.data.map(line => ({
          label: line.lineName,
          value: line.lineCode,
          children: line.subwayList.map(station => ({
            label: station.subwayName,
            value: station.subwayCode
          }))
        }));
        setSubwayOptions(subwayList);
        
        // 构建地铁站代码到名称的映射
        const subwayMapping = {};
        subwayResult.data.forEach(line => {
          line.subwayList.forEach(station => {
            subwayMapping[station.subwayCode] = `${line.lineName} ${station.subwayName}`;
          });
        });
        setSubwayMap(subwayMapping);
      }
    } catch (error) {
      console.error('获取筛选选项失败:', error);
    }
  };

  useEffect(() => {
    fetchApartmentList();
    fetchOptions();
  }, []);

  // 处理搜索
  const handleSearch = () => {
    const values = form.getFieldsValue();
    const filters = {};
    
    // 公寓名称 -> keyword
    if (values.name) {
      filters.keyword = values.name;
    }
    
    // 行政区 -> districtCode
    if (values.district) {
      filters.districtCode = values.district;
    }
    
    // 地铁站 -> subwayCode（取级联选择器的第二个值）
    if (values.subway && values.subway.length > 0) {
      filters.subwayCode = values.subway[1]; // 地铁站代码
    }
    
    // 维护人 -> uploaderPhone
    if (values.uploader) {
      filters.uploaderPhone = values.uploader;
    }
    
    fetchApartmentList(1, pagination.pageSize, filters);
  };

  // 重置筛选
  const handleReset = () => {
    form.resetFields();
    fetchApartmentList(1, pagination.pageSize);
  };

  // 处理表格分页变化
  const handleTableChange = (newPagination) => {
    const values = form.getFieldsValue();
    const filters = {};
    
    // 公寓名称 -> keyword
    if (values.name) {
      filters.keyword = values.name;
    }
    
    // 行政区 -> districtCode
    if (values.district) {
      filters.districtCode = values.district;
    }
    
    // 地铁站 -> subwayCode（取级联选择器的第二个值）
    if (values.subway && values.subway.length > 0) {
      filters.subwayCode = values.subway[1];
    }
    
    // 上传人手机号 -> uploaderPhone
    if (values.uploader) {
      filters.uploaderPhone = values.uploader;
    }
    
    fetchApartmentList(newPagination.current, newPagination.pageSize, filters);
  };

  // 打开新增/编辑弹窗
  const handleOpenModal = async (record = null) => {
    if (record) {
      // 编辑模式：先获取房源详情
      try {
        setLoading(true);
        const result = await getApartmentDetail(record.id);
        if (result.code === 200 && result.data) {
          setEditingRecord(result.data);
          setModalVisible(true);
        } else {
          message.error('获取房源详情失败');
        }
      } catch (error) {
        message.error('获取房源详情失败');
      } finally {
        setLoading(false);
      }
    } else {
      // 新增模式
      setEditingRecord(null);
      setModalVisible(true);
    }
  };

  // 关闭弹窗
  const handleCloseModal = () => {
    setEditingRecord(null);
    setModalVisible(false);
  };

  // 保存成功后刷新列表
  const handleSaveSuccess = () => {
    handleCloseModal();
    fetchApartmentList(pagination.current, pagination.pageSize);
  };

  // 删除房源
  const handleDelete = async (id) => {
    try {
      const result = await deleteApartment(id);
      if (result.code === 200) {
        message.success('删除成功');
        fetchApartmentList(pagination.current, pagination.pageSize);
      } else {
        message.error(result.msg || '删除失败');
      }
    } catch (error) {
      message.error('删除失败');
    }
  };

  const columns = [
    {
      title: '房源编号',
      dataIndex: 'id',
      key: 'id',
      width: 120,
    },
    {
      title: '公寓名称',
      dataIndex: 'houseName',
      key: 'houseName',
      width: 150,
    },
    {
      title: '所属行政区',
      dataIndex: 'districtAmapCode',
      key: 'districtAmapCode',
      width: 120,
      render: (districtAmapCode) => districtMap[districtAmapCode] || districtAmapCode
    },
    {
      title: '地铁站',
      dataIndex: 'subwayAmapCode',
      key: 'subwayAmapCode',
      width: 150,
      render: (subwayAmapCode) => subwayMap[subwayAmapCode] || subwayAmapCode || '-'
    },
    {
      title: '价格（元/月）',
      key: 'price',
      width: 150,
      render: (_, record) => {
        const { minPrice, maxPrice } = record;
        if (minPrice && maxPrice) {
          return `¥${minPrice}~¥${maxPrice}`;
        } else if (minPrice) {
          return `¥${minPrice}`;
        } else if (maxPrice) {
          return `¥${maxPrice}`;
        } else {
          return '-';
        }
      }
    },
    {
      title: '上传日期',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
    },
    {
      title: '最后维护日期',
      dataIndex: 'updateAt',
      key: 'updateAt',
      width: 130,
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 150,
      render: (_, record) => (
        <Space size="middle">
          <Button 
            type="link" 
            icon={<EditOutlined />}
            onClick={() => handleOpenModal(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个房源吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button 
              type="link" 
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="apartment-list">
      <Card className="search-card">
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="name" label="公寓名称">
                <Input placeholder="请输入公寓名称" allowClear />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="district" label="行政区">
                <Select placeholder="请选择行政区" allowClear>
                  {districtOptions.map(item => (
                    <Select.Option key={item.value} value={item.value}>
                      {item.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="subway" label="地铁站">
                <Cascader
                  options={subwayOptions}
                  placeholder="请选择地铁站"
                  allowClear
                  showSearch
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="uploader" label="上传人手机号">
                <Input placeholder="请输入上传人手机号" allowClear />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col span={24} style={{ textAlign: 'right' }}>
              <Space>
                <Button icon={<ReloadOutlined />} onClick={handleReset}>
                  重置
                </Button>
                <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                  查询
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </Card>

      <div className="list-header">
        <h2>房源管理</h2>
        <Button 
          type="primary" 
          icon={<PlusOutlined />}
          onClick={() => handleOpenModal()}
        >
          上传新房源
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={dataSource}
        rowKey="id"
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        scroll={{ x: 1200 }}
      />
      <ApartmentModal
        visible={modalVisible}
        record={editingRecord}
        onCancel={handleCloseModal}
        onSuccess={handleSaveSuccess}
      />
    </div>
  );
};

export default ApartmentList;



